import { InfiniteData, useInfiniteQuery, UseInfiniteQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';

import { getIdentitySuggestions, normalizeSid } from '../api/identity';
import { ApiError } from '../api/client';
import {
  IdentitySummary,
  IdentitySuggestionsData,
  IdentitySuggestionUser,
} from '../types/identity';

// Server max per the FD-103 contract (top default 25, max 50). Request the max
// so the device-grid chip join covers as many unique users as possible per page.
const PAGE_SIZE = 50;

export type IdentitySuggestionsQuery = UseInfiniteQueryResult<
  InfiniteData<IdentitySuggestionsData>,
  Error
>;

// Shared identity-suggestions query (FD-103 v1.0). Both MigrationDetailPage
// (device-grid chip derivation) and IdentityMappingsSection (working table)
// call this hook with the same key — react-query dedupes to a single fetch
// and both consumers see the same cache entry.
export function useIdentitySuggestions(
  migrationId: string | undefined,
  enabled: boolean,
  search?: string | null,
): IdentitySuggestionsQuery {
  // FD-508 — the search term is part of the key only WHEN THERE IS ONE, so an
  // unsearched feed keeps the byte-identical key every existing caller already
  // uses (`MigrationDetailPage`'s device-grid chip join, and the invalidations
  // in `useIdentityWorkbench`). A searched feed is a different dataset and must
  // not be served to the chip join; react-query invalidation is prefix-based,
  // so `['identity-suggestions', migrationId]` still clears both.
  const trimmedSearch = search?.trim() ?? '';
  const queryKey = trimmedSearch
    ? (['identity-suggestions', migrationId, { search: trimmedSearch }] as const)
    : (['identity-suggestions', migrationId] as const);

  return useInfiniteQuery<IdentitySuggestionsData, Error>({
    queryKey: [...queryKey],
    queryFn: ({ pageParam }) =>
      getIdentitySuggestions(migrationId!, {
        top: PAGE_SIZE,
        search: trimmedSearch || undefined,
        continuationToken: (pageParam as string | null | undefined) ?? undefined,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.continuationToken ?? undefined,
    enabled: Boolean(migrationId) && enabled,
    retry: (failureCount, error) => {
      // Consent 409s and 404s are deterministic — retrying re-runs the same
      // Graph-consent check for nothing. 502 GRAPH_ERROR already survived a
      // server-side retry-once; give it one client retry.
      if (error instanceof ApiError && (error.status === 409 || error.status === 404)) {
        return false;
      }
      return failureCount < 1;
    },
  });
}

// Flattens the loaded pages into rows and an index keyed by normalized SID —
// the FD-103 join key between device-grid rows (FD-102 primaryUserSid) and
// mapping-section rows.
export function useSuggestionRows(query: IdentitySuggestionsQuery): {
  rows: IdentitySuggestionUser[];
  bySid: Map<string, IdentitySuggestionUser>;
  devicesWithoutManifest: number;
  totalUniqueUsers: number;
  /** FD-506 campaign-wide buckets; undefined against a Manager predating it. */
  summary?: IdentitySummary;
  /** FD-506 — how many unique users matched the active search, if any. */
  matchedUserCount?: number;
} {
  const pages = query.data?.pages;
  return useMemo(() => {
    const rows = pages ? pages.flatMap((p) => p.users) : [];
    const bySid = new Map<string, IdentitySuggestionUser>();
    for (const row of rows) bySid.set(normalizeSid(row.sid), row);
    // FD-508 — read from the FIRST page, not the last. Every page carries the
    // same campaign-wide figures, so taking them from whichever page happened
    // to load most recently was fragile for no reason; the first page is the
    // one that always exists.
    const first = pages && pages.length > 0 ? pages[0] : undefined;
    return {
      rows,
      bySid,
      devicesWithoutManifest: first?.devicesWithoutManifest ?? 0,
      totalUniqueUsers: first?.summary?.totalUniqueUsers ?? first?.totalUniqueUsers ?? 0,
      summary: first?.summary,
      matchedUserCount: first?.matchedUserCount,
    };
  }, [pages]);
}
